import { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface SectionTooltipProps {
  content: string;
  disabled?: boolean;
  color?: string;
}

export function SectionTooltip({
  content,
  disabled = false,
  color = "var(--muted-foreground)",
}: SectionTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<
    "left" | "center" | "right"
  >("center");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (
      event: MouseEvent | TouchEvent,
    ) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
      document.removeEventListener(
        "touchstart",
        handleClickOutside,
      );
    };
  }, [isOpen]);

  // Calculate tooltip position to keep it in viewport
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const buttonRect =
      buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 280; // max-width of tooltip

    // Calculate if tooltip would overflow on right
    const wouldOverflowRight =
      buttonRect.left + tooltipWidth / 2 > viewportWidth - 16;
    // Calculate if tooltip would overflow on left
    const wouldOverflowLeft =
      buttonRect.left - tooltipWidth / 2 < 16;

    if (wouldOverflowRight) {
      setPosition("right");
    } else if (wouldOverflowLeft) {
      setPosition("left");
    } else {
      setPosition("center");
    }
  }, [isOpen]);

  const handleToggle = (
    e: React.MouseEvent | React.TouchEvent,
  ) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Calculate tooltip positioning styles based on position
  const getTooltipStyles = () => {
    const baseStyles = {
      position: "absolute" as const,
      top: "calc(100% + 8px)",
      zIndex: 1000,
      minWidth: "200px",
      maxWidth: "280px",
      background:
        "linear-gradient(135deg, var(--kojo-blue) 0%, var(--kojo-blue-dark) 100%)",
      border: "none",
      borderRadius: "16px",
      padding: "12px",
      boxShadow:
        "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)",
    };

    if (position === "right") {
      return {
        ...baseStyles,
        right: "0",
      };
    } else if (position === "left") {
      return {
        ...baseStyles,
        left: "0",
      };
    } else {
      return {
        ...baseStyles,
        left: "50%",
        transform: "translateX(-50%)",
      };
    }
  };

  // Calculate arrow position based on tooltip position
  const getArrowStyles = () => {
    const baseStyles = {
      position: "absolute" as const,
      top: "-5px",
      width: "10px",
      height: "10px",
      background:
        "linear-gradient(135deg, var(--kojo-blue) 0%, var(--kojo-blue-dark) 100%)",
      border: "none",
      borderRight: "none",
      borderBottom: "none",
    };

    if (position === "right") {
      return {
        ...baseStyles,
        right: "12px",
        transform: "rotate(45deg)",
      };
    } else if (position === "left") {
      return {
        ...baseStyles,
        left: "12px",
        transform: "rotate(45deg)",
      };
    } else {
      return {
        ...baseStyles,
        left: "50%",
        transform: "translateX(-50%) rotate(45deg)",
      };
    }
  };

  return (
    <div
      ref={tooltipRef}
      style={{ position: "relative", display: "inline-flex" }}
    >
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          border: "none",
          backgroundColor: "transparent",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 0.7,
          transition:
            "opacity 0.2s ease, background-color 0.2s ease",
          padding: "0",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.backgroundColor =
              "rgba(0, 0, 0, 0.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.opacity = "0.7";
            e.currentTarget.style.backgroundColor =
              "transparent";
          }
        }}
        aria-label="Informações sobre esta seção"
      >
        <Info
          style={{
            width: "18px",
            height: "18px",
            color: color,
          }}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{
              duration: 0.15,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            style={getTooltipStyles()}
          >
            {/* Arrow */}
            <div style={getArrowStyles()} />

            <p
              style={{
                fontSize: "14px",
                fontWeight: "var(--font-weight-normal)",
                lineHeight: "1.5",
                color: "white",
                margin: "0",
              }}
            >
              {content}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}